import React, { useEffect, useState } from 'react';
import {
  X,
  Star,
  CalendarClock,
  Sparkles,
  ArrowRight, ArrowLeft, AudioLines, SpeechIcon, MessageCircle, Play
} from 'lucide-react';
import './FeedbackModal.css';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toaster/use-toast';
import { scheduleTrainingPlan, startTraining } from '../../api/trainingApi';
import { BASE_URL } from '../../config/constants';
import MeetingRecordingPlayer from '../MeetingRecordingPlayer/MeetingRecordingPlayer';
import MetricChartCard from '../MetricChartCard/MetricChartCard';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import successCheckLottie from '@teleprompt/shared/assets/animations/Success-Check.lottie';
import SchedulePickerModal from '../SchedulePickerModal/SchedulePickerModal';
import { toISODate, makeISO } from '../../utils/schedule';
import TrainingCard from '../TrainingCard/TrainingCard';
import FeedbackSummaryContent from './FeedbackSummaryContent';
import FeedbackMarkdownText from './FeedbackMarkdownText';
import { canUseTrainingPlans } from '../../utils/billingPlans';
import { useSelector } from 'react-redux';
import StreakCompletionStep from '../Streak/StreakCompletionStep';

function toPaceTag(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function toDisplayFillerWord(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = raw.toLowerCase();
  if (["none", "n/a", "na", "null"].includes(normalized)) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function getPacingBadge(wpm) {
  const value = Number(wpm);
  if (!Number.isFinite(value)) return "";
  if (value < 140) return "Too Slow";
  if (value > 160) return "Too Fast";
  return "Well Paced";
}

function hasPlaybackTimestamp(value) {
  return value != null && Number.isFinite(Number(value)) && Number(value) >= 0;
}

export default function FeedbackModal({
  meetingData,
  feedback,
  trainingPlan = [],
  recording,
  recordingAnalysis,
  streak,
  error,
  onClose
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = useSelector((s) => s.user?.data);
  const planCode = user?.billing?.plan?.code || user?.entitlements?.planCode || '';
  const planLabel = user?.billing?.plan?.name || user?.entitlements?.planName || '';
  const trainingPlansEnabled = canUseTrainingPlans(planCode, planLabel);
  const [showStreak, setShowStreak] = useState(Boolean(streak));
  const [showTraining, setShowTraining] = useState(false);
  const [scheduling, setScheduling] = useState(null);
  const [dateISO, setDateISO] = useState('');
  const [timeHHmm, setTimeHHmm] = useState('');
  const [scheduledMap, setScheduledMap] = useState({});
  const todayISO = toISODate(new Date());
  // open
  const openSchedule = (step) => { setScheduling(step); setDateISO(''); setTimeHHmm(''); };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const nextWeek = new Date(); nextWeek.setDate(today.getDate() + 7);
  // save
  const handleSchedule = async () => {
    try {
      const iso = makeISO(dateISO, timeHHmm);
      if (!iso) {
        toast({ title: 'Pick date & time', description: 'Please choose both a date and a time.', variant: 'destructive' });
        return;
      }
      if (new Date(iso) <= new Date()) {
        toast({ title: 'Invalid time', description: 'Please pick a future time.', variant: 'destructive' });
        return;
      }
      const { plan } = await scheduleTrainingPlan(scheduling.id, iso);

      // mark scheduled locally
      setScheduledMap(prev => ({ ...prev, [scheduling.id]: iso }));

      setScheduling(null);
      toast({
        title: 'Scheduled',
        description: `Training "${scheduling.title}" set for ${new Date(plan.scheduled_time).toLocaleString()}.`
      });
    } catch (err) {
      console.error(err);
      toast({ title: 'Could not schedule', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleStart = async (step) => {
    if (!trainingPlansEnabled) {
      onClose();
      navigate('/pricing');
      return;
    }

    try {
      console.log("training plan id", step.id)
      await startTraining(step.id);
      onClose();
      navigate(
        `/meeting/${meetingData.id}`,
        {
          state: {
            meetingData,
            trainingPlanId: step.id
          }
        }
      );
      toast({
        title: `Started: ${step.title}`,
        description: `Good luck practicing this skill!`,
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to start training:', error);
    }
  }


  const [bounce, setBounce] = useState(false);
  const [seekTarget, setSeekTarget] = useState({ seconds: null, token: 0 });

  const authenticatedRecordingSrc = meetingData?.id
    ? `${BASE_URL}/api/meeting/${meetingData.id}/recording`
    : "";

  const strengthHighlights = Array.isArray(recordingAnalysis?.highlights?.strengths)
    ? recordingAnalysis.highlights.strengths
    : [];
  const improvementHighlights = Array.isArray(recordingAnalysis?.highlights?.improvements)
    ? recordingAnalysis.highlights.improvements
    : [];

  const jumpToSnippet = (seconds) => {
    const nextSeconds = Number(seconds);
    if (!Number.isFinite(nextSeconds)) return;
    setSeekTarget((prev) => ({ seconds: nextSeconds, token: prev.token + 1 }));
  };
  const pacingWpmValue = Number(recordingAnalysis?.pacing?.wordsPerMinute);
  const pacingWpmRounded = Number.isFinite(pacingWpmValue) ? Math.round(pacingWpmValue) : null;
  const pacingBadge = getPacingBadge(pacingWpmRounded);
  const fillerCountRaw = Number(recordingAnalysis?.filler?.count);
  const fillerCount = Number.isFinite(fillerCountRaw) && fillerCountRaw > 0
    ? Math.round(fillerCountRaw)
    : 0;
  const fillerWord = toDisplayFillerWord(recordingAnalysis?.filler?.topWord);
  const hasTopFiller = fillerCount > 0 && Boolean(fillerWord);
  const fillerMetricValue = hasTopFiller ? fillerWord : "None";
  const fillerMetricDescription = hasTopFiller
    ? `Said ${fillerCount} ${fillerCount === 1 ? "time" : "times"}`
    : null;
  const pacingSeries = (() => {
    if (Array.isArray(recordingAnalysis?.pacing?.series)) {
      const seriesValues = recordingAnalysis.pacing.series
        .map((point) => Number(point?.wordsPerMinute))
        .filter((value) => Number.isFinite(value) && value > 0);
      if (seriesValues.length > 0) return seriesValues;
    }
    if (pacingWpmRounded == null) return [];
    return [
      pacingWpmRounded * 0.93,
      pacingWpmRounded * 1.04,
      pacingWpmRounded * 0.97,
      pacingWpmRounded,
    ].map((value) => Number(value.toFixed(1)));
  })();
  const hasMetricsCards = pacingWpmRounded != null || Boolean(recordingAnalysis);
  useEffect(() => {
    const scrollable = document.querySelector(".modal-scrollable");
    if (!scrollable) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollable;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // near bottom
      if (isAtBottom) setBounce(true);
    };

    scrollable.addEventListener("scroll", handleScroll);
    return () => scrollable.removeEventListener("scroll", handleScroll);
  }, []);

  // Training session button text
  const trainingBtn =
    trainingPlan.length === 1
      ? 'Your Training Session'
      : 'Your Training Sessions';

  return (
    <>
      <div className="modal-backdrop" onClick={handleBackdropClick}>

        <div
          className="modal-content modal-content-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-hero-row">
            <div className="modal-hero__center">
              {error ? (
                <>
                  <h2 className="modal-hero__title" style={{ color: "var(--error-fg)" }}>
                    Something Went Wrong
                  </h2>

                </>
              ) : (
                <>
                  <h2 className="modal-hero__title">
                    {showStreak
                      ? 'Session Complete'
                      : showTraining
                        ? 'Your Training Plan'
                        : 'Feed Forward'}
                  </h2>
                  {!showStreak && !showTraining ? (
                    <div className="modal-hero__anim" aria-hidden="true">
                      <DotLottieReact
                        src={successCheckLottie}
                        autoplay
                        loop={false}
                        style={{ width: 80, height: 80 }}
                      />
                    </div>
                  ) : null}
                </>
              )}
            </div>
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close feedback modal"
            >
              <X size={24} />
            </button>
          </div>


          {error ? (
            <div className="modal-error-section">
              <p className="modal-error-message">{error}</p>
              <button
                className="button button-primary retry-button"
                onClick={() => window.dispatchEvent(new CustomEvent("retry-feedback"))}
              >
                Retry
              </button>
            </div>
          ) : (

            <> <div className="modal-scrollable">
              {showStreak ? (
                <StreakCompletionStep
                  streak={streak}
                  onContinue={() => setShowStreak(false)}
                />
              ) : !showTraining ? (
                <>
                  <div className="modal-section modal-section-animated feedback-summary" style={{ animationDelay: '70ms' }}>
                    <h4 className="section-header">
                      <MessageCircle size={20} color="#72b4fe" fill='#72b4fe' />
                      Summary
                    </h4>
                    <FeedbackSummaryContent text={feedback} />
                  </div>

                  {recording?.recordingUrl && (
                    <div className="modal-section modal-section-animated feedback-recording" style={{ animationDelay: '90ms' }}>
                      <h4 className="section-header">
                        <AudioLines size={20} color="#72b4fe" />
                        Session Recording
                      </h4>
                      <MeetingRecordingPlayer
                        src={authenticatedRecordingSrc}
                        durationSeconds={recording?.durationSeconds}
                        seekToSeconds={seekTarget.seconds}
                        seekToken={seekTarget.token}
                        autoPlayOnSeek
                      />
                    </div>
                  )}

                  {recordingAnalysis && (
                    <div className="modal-section modal-section-animated recording-highlights-section" style={{ animationDelay: '100ms' }}>
                      <h4 className="section-header">
                        <SpeechIcon size={20} color="#72b4fe" />
                        Audio Highlights
                      </h4>

                      <div className="recording-highlight-sections">
                        <div className="recording-highlight-subsection">
                          <h5 className="recording-highlight-subtitle">What Went Well</h5>
                          {strengthHighlights.length > 0 ? (
                            strengthHighlights.map((item, idx) => (
                              <div className="recording-highlight-card recording-highlight-card--positive" key={`good-${idx}`}>
                                <div className="recording-highlight-card-header">
                                  {hasPlaybackTimestamp(item.startSeconds) ? (
                                    <button
                                      type="button"
                                      className="recording-highlight-jump recording-highlight-jump--positive"
                                      onClick={() => jumpToSnippet(item.startSeconds)}
                                      aria-label="Play highlight moment"
                                    >
                                      <Play size={18} fill="currentColor" />
                                    </button>
                                  ) : null}
                                  <FeedbackMarkdownText
                                    className="recording-highlight-title"
                                    inline
                                    text={item.title}
                                  />
                                </div>
                                <FeedbackMarkdownText
                                  className="recording-highlight-reason"
                                  text={item.reason}
                                />
                              </div>
                            ))
                          ) : (
                            <p className="recording-highlight-empty">No strengths snippets yet.</p>
                          )}
                        </div>

                        <div className="recording-highlight-subsection">
                          <h5 className="recording-highlight-subtitle">Growth Areas</h5>
                          {improvementHighlights.length > 0 ? (
                            improvementHighlights.map((item, idx) => (
                              <div className="recording-highlight-card recording-highlight-card--improve" key={`improve-${idx}`}>
                                <div className="recording-highlight-card-header">
                                  {hasPlaybackTimestamp(item.startSeconds) ? (
                                    <button
                                      type="button"
                                      className="recording-highlight-jump recording-highlight-jump--improve"
                                      onClick={() => jumpToSnippet(item.startSeconds)}
                                      aria-label="Play improvement moment"
                                    >
                                      <Play size={18} fill="currentColor" />
                                    </button>
                                  ) : null}
                                  <FeedbackMarkdownText
                                    className="recording-highlight-title"
                                    inline
                                    text={item.title}
                                  />
                                </div>
                                <FeedbackMarkdownText
                                  className="recording-highlight-reason"
                                  text={item.reason}
                                />
                              </div>
                            ))
                          ) : (
                            <p className="recording-highlight-empty">No improvement snippets yet.</p>
                          )}
                        </div>
                      </div>

                      {hasMetricsCards ? (
                        <div className="recording-metrics-section">
                          <h5 className="recording-metrics-title">Metrics</h5>
                          <div className="recording-metrics-grid">
                            {pacingWpmRounded != null ? (
                              <MetricChartCard
                                label="Pacing"
                                value={`${pacingWpmRounded} Words Per Minute`}
                                type="line"
                                series={pacingSeries}
                                tag={pacingBadge || toPaceTag(recordingAnalysis?.pacing?.paceLabel)}
                              />
                            ) : null}
                            <MetricChartCard
                              label="Top Filler Word"
                              value={fillerMetricValue}
                              type="text"
                              description={fillerMetricDescription}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                  {/* ───────────────────────────── */}
                  {/* 🌟 Next Steps Snapshot (mirrors Emotion Analysis styling) */}
                  {/* ───────────────────────────── */}
                  {trainingPlan?.length > 0 && (
                    <div
                      className="modal-section-animated next-steps-section"
                      style={{
                        animationDelay: '360ms',
                        textAlign: 'left',
                      }}
                    >
                      <h3 className="feedback-header" style={{ justifyContent: 'center' }}>
                        <Sparkles size={20} style={{ color: 'var(--foreground)' }} />
                        Next Steps
                      </h3>

                      <p
                        style={{
                          marginTop: '6px',
                          marginBottom: '1rem',
                          color: 'var(--foreground)',
                          fontSize: '1rem',
                          lineHeight: '1.6',
                        }}
                      >
                        To improve your communication and achieve your goals, here is your training plan focusing on:
                      </p>

                      <div className="next-steps-list">
                        {trainingPlan.slice(0, 3).map((plan, i) => (
                          <div key={plan.id} className="next-step-item">
                            <Star size={18} style={{ color: '#f2b92d', fill: '#f2b92d' }} />
                            <span>{plan.title}</span>
                          </div>
                        ))}
                      </div>

                      <p className="next-steps-footer">
                        To <strong>View Your Training Sessions</strong> tap below.
                      </p>
                    </div>
                  )}




                </>
              ) : (
                <>

                  <div className="training-plan-section">
                    <h3 style={{ fontSize: '1.4rem' }}>
                      <CalendarClock size={22} style={{ verticalAlign: "middle", marginRight: "0.5rem" }} />
                      Personalized Training Plan
                    </h3>
                    <span>   Here's how you performed and ways to improve</span>
                    <div className="card-list">
                      {trainingPlan.map((step) => {
                        const status = step.status || "Not Started";
                        const created = step.createdAt
                          ? new Date(step.createdAt).toLocaleDateString()
                          : "—";
                        const duration = step.duration
                          ? `${step.duration} min`
                          : "—";

                        return (
                          <TrainingCard
                            key={step.id}
                            plan={step}
                            status={status}
                            created={created}
                            duration={duration}
                            onOpenSchedule={() => openSchedule(step)}
                            onPlanAction={(plan) => handleStart(plan)}
                            canUseTrainingPlans={trainingPlansEnabled}
                          />
                        );
                      })}
                    </div>
                  </div>

                </>
              )}


            </div>
              {!showStreak ? <div className="button-actions-row">
                {!showTraining ? (
                  <>
                    {/* <button className="button button-outline" onClick={onClose}>Close</button> */}
                    {trainingPlan?.length > 0 && (
                      <button
                        className={`button button-primary training-button`}
                        id="viewTrainingButton"
                        onClick={() => setShowTraining(true)}
                      >
                        {trainingBtn}
                        <ArrowRight size={20} />
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button className="button button-outline" onClick={() => setShowTraining(false)}>
                      <ArrowLeft size={20} />
                      Back to Feedback
                    </button>
                    {/* <button className="button button-primary" onClick={onClose}>
                    Exit
                  </button> */}
                  </>
                )}
              </div> : null}
            </>

          )}
        </div>
      </div>
      {scheduling && (
        <SchedulePickerModal
          open={Boolean(scheduling)}
          title="Schedule Session"
          dateISO={dateISO}
          timeHHmm={timeHHmm}
          onChangeDate={setDateISO}
          onChangeTime={setTimeHHmm}
          minDateISO={todayISO}
          onClose={() => setScheduling(null)}
          onSave={handleSchedule}
        />
      )}

    </>

  );
}
